import { computed, inject, Injectable, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '../../environments/environment';
import { faro } from '@grafana/faro-web-sdk';
import {
  FILM_BASE_PATH,
  FILM_POST_LOGIN_URL_KEY,
  OIDC_CODE_FLOW_IN_PROGRESS,
  PING_CONFIG_ID,
  REDIRECT_FLAG,
  SSO_TARGET_URL_KEY,
} from '../constants/auth.constants';

/**
 * How far the SSO round-trip has got, from the shell's point of view.
 *
 * - `pending` — we do not yet know whether this user may see the app. Either
 *   the bootstrap auth check came back unauthenticated and the guard has not
 *   ruled yet, or
 *   a `?code=` exchange is still in flight.
 * - `authenticated` — the only phase in which the shell paints.
 * - `redirecting` — the guard handed the browser to Ping; the tab is on its way out.
 * - `failed` — we came back from Ping still unauthenticated and the loop-breaker
 *   tripped. Nothing more will happen on its own.
 */
export type AuthPhase = 'pending' | 'authenticated' | 'redirecting' | 'failed';

/**
 * How long the shell waits for a verdict before giving up and offering a retry.
 * Generous: a healthy redirect to Ping unloads the tab in well under a second.
 */
export const PENDING_TIMEOUT_MS = 15_000;

// Standard OIDC claims returned by Ping (wamqa.roche.com).
type PingIdentity = {
  email?: string;
  given_name?: string;
  family_name?: string;
  username?: string;
  sub?: string;
  // Authorization groups. Ping returns a scalar string for a single group,
  // an array for multiple, and omits the claim when the user has none.
  groups?: string | string[];
};

// Mock identity used for local work (Ping redirect flow does not run on localhost).
type MockIdentity = {
  email?: string;
  sub?: string;
  groups?: string[];
  custom?: {
    id?: string;
    first?: string;
    last?: string;
    isModerator?: boolean;
  };
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _document = inject(DOCUMENT);
  private readonly _oidc = inject(OidcSecurityService);
  private readonly _router = inject(Router);

  private readonly _ready = signal(false);
  private readonly _phase = signal<AuthPhase>('pending');
  private readonly _email = signal<string | null>(null);
  private readonly _first = signal<string | null>(null);
  private readonly _last = signal<string | null>(null);
  private readonly _cid = signal<string | null>(null);
  private readonly _sub = signal<string | null>(null);
  private readonly _groups = signal<string[]>([]);

  private _watchdog?: number;

  readonly ready = computed(() => this._ready());

  /**
   * How far the SSO round-trip has got.
   *
   * `ready` cannot stand in for this: it flips inside `refresh()`, which runs in
   * an app initializer, so it is already true by the time anything renders. The
   * question the shell needs answered — "will this tab still be here in a
   * second?" — is only settled once the guard has ruled.
   */
  readonly phase = this._phase.asReadonly();

  readonly email = computed(() => this._email());
  readonly first = computed(() => this._first());
  readonly last = computed(() => this._last());
  readonly cId = computed(() => this._cid());

  /**
   * The SSO-issued `sub` claim — opaque, stable and unique per environment.
   *
   * This is the identifier for observability and audit. It is deliberately not
   * `cId`: `cId` prefers the `username` claim, which is a Roche short ID and
   * therefore re-identifying, and it feeds the fulfillment payload where a
   * human-readable requester is required. Keep the two apart.
   */
  readonly sub = this._sub.asReadonly();
  readonly groups = computed(() => this._groups());

  // True when the user belongs to the environment's moderator group.
  // Moderators get a platform-wide view (e.g. all offerings in FOCUS).
  readonly isModerator = computed(() =>
    this._groups().includes(environment.moderatorGroup),
  );

  readonly initials = computed(() => {
    const f = (this._first() ?? '').trim();
    const l = (this._last() ?? '').trim();
    const fromNames = ((f[0] ?? '') + (l[0] ?? '')).toUpperCase();
    if (fromNames) return fromNames;
    // fallback
    return '';
  });

  async refresh(): Promise<void> {
    // These trace the SSO round-trip, which is the most failure-prone path in
    // the app — keep them. They must stay free of claims: Faro's console
    // instrumentation forwards `info` in every build with `production: false`,
    // so anything logged here reaches Grafana on dev, test and local.
    console.info('[SSO][refresh] start', {
      pingAuthInit: environment.pingAuthInit,
      browserUrl: this._document.location.href,
    });
    try {
      if (!environment.pingAuthInit) {
        this.applyMockIdentity(environment.pingAuthMock as MockIdentity);
        // Local/mock mode never redirects, so the shell can paint immediately.
        this._phase.set('authenticated');
        return;
      }

      this.clearStaleCodeFlowFlag();

      // `checkAuth()` only reads storage: when the stored access token has
      // expired it reports `isAuthenticated: false` and never touches the
      // refresh token sitting beside it, so a reload a few minutes into a
      // session bounced the user back to Ping. The periodic renew only covers a
      // live app instance; a reload kills it and bootstrap has to stand in.
      // `checkAuthIncludingServer()` falls through to `forceRefreshSession()`.
      //
      // Only worth asking the server when there is something to spend, though.
      // On empty storage that call *throws* ("no refresh token found") instead
      // of returning `isAuthenticated: false`, which would route every
      // anonymous first visit into the catch below and push an application
      // error to Faro — on production too, where ERROR is always forwarded.
      const storedRefreshToken = await firstValueFrom(
        this._oidc.getRefreshToken(),
      );
      const loginResponse = await firstValueFrom(
        storedRefreshToken
          ? this._oidc.checkAuthIncludingServer()
          : this._oidc.checkAuth(),
      );

      const { isAuthenticated, userData } = loginResponse;

      if (!isAuthenticated) {
        // Stay `pending`: the guard decides whether this becomes a redirect to
        // Ping, a wait for an in-flight code exchange, or an outright failure.
        console.info('[SSO][refresh] not authenticated -> reset()');
        this.reset();
        return;
      }

      this._phase.set('authenticated');
      this.applyIdentity(userData as PingIdentity);

      // A login FILM started lands here too; send it back before restoring
      // anything of our own.
      if (this.handOffToFilm()) return;
      this.restoreDeepLinkTarget();
    } catch (err) {
      // Surfaces errors that would otherwise be swallowed into a blank page.
      // Only the message goes to the console: a rejection from the auth check
      // can carry the token endpoint's response body, claims and all, and
      // console.info is forwarded to Faro wherever `production` is false.
      console.info(
        '[SSO][refresh] threw -> reset()',
        err instanceof Error ? err.message : String(err),
      );
      faro?.api?.pushError(err instanceof Error ? err : new Error(String(err)));
      this.reset();
    } finally {
      this._ready.set(true);
      // Mock mode has no guard reporting behind it — the guard returns early
      // without ruling — so a throw in `applyMockIdentity` would otherwise
      // strand local dev on the sign-in screen with no way out.
      if (!environment.pingAuthInit) {
        this._phase.set('authenticated');
      }

      if (this._phase() === 'authenticated') {
        this.clearPendingWatchdog();
      } else {
        this.armPendingWatchdog();
      }
      console.info('[SSO][refresh] done, ready=true');
    }
  }

  /**
   * The verdict from `authGuard`, which is the only observer of three of these
   * four outcomes.
   *
   * `refresh()` already reports `authenticated` when the bootstrap check
   * succeeds, but
   * a silent renew can authenticate the user *after* bootstrap — without the
   * guard repeating it, the route would activate behind a shell still showing
   * the sign-in screen.
   *
   * Single entry point on purpose: the shell stays hidden until something lands
   * here, so a fifth outcome added later has to pass through one place rather
   * than needing its own setter and its own call site.
   */
  reportDecision(phase: Exclude<AuthPhase, 'pending'>): void {
    if (phase !== 'redirecting') {
      // `redirecting` is not an ending — the tab may yet fail to leave.
      this.clearPendingWatchdog();
    }
    this._phase.set(phase);
  }

  /**
   * Manual retry from the failure screen. Clears the loop-breaker first —
   * otherwise the guard would refuse to redirect a second time.
   */
  retryLogin(): void {
    this._document.defaultView?.sessionStorage?.removeItem(REDIRECT_FLAG);
    this._phase.set('redirecting');
    this.armPendingWatchdog();
    this._oidc.authorize();
  }

  /**
   * Clears a `storageCodeFlowInProgress` left behind by an abandoned login.
   *
   * The library sets the flag in `authorize()` and clears it only when a
   * callback lands. It has no staleness timeout — unlike
   * `storageSilentRenewRunning`, which self-heals — and
   * `shouldStartPeriodicallyCheckForConfig()` refuses to start silent renew
   * while it is set.
   *
   * In sessionStorage that was self-limiting: the flag died with the tab. Now
   * that tokens live in localStorage so they survive a new tab, the flag
   * survives too — a user who closes the tab on the Ping sign-in page would
   * carry it across browser restarts and have token renewal quietly disabled
   * from then on. If we are plainly not mid-callback, the flag is stale.
   */
  private clearStaleCodeFlowFlag(): void {
    const storage = this._document.defaultView?.localStorage;
    if (!storage) return;
    if (this._document.location.search.includes('code=')) return;

    const raw = storage.getItem(PING_CONFIG_ID);
    if (!raw) return;

    try {
      const blob = JSON.parse(raw) as Record<string, unknown>;
      if (!blob[OIDC_CODE_FLOW_IN_PROGRESS]) return;

      delete blob[OIDC_CODE_FLOW_IN_PROGRESS];
      storage.setItem(PING_CONFIG_ID, JSON.stringify(blob));
      console.info('[SSO][refresh] cleared stale code-flow-in-progress flag');
    } catch {
      // Unparseable blob: leave it alone. The auth check below will fail to
      // read tokens out of it and reset the entry properly.
    }
  }

  /**
   * Nothing guarantees a verdict ever arrives: the `?code=` exchange can hang,
   * and `authorize()` can fail to navigate. Either way the user would sit on an
   * indefinite spinner with no control on screen. Fall back to the failure
   * screen, which at least offers a retry.
   */
  private armPendingWatchdog(): void {
    this.clearPendingWatchdog();
    this._watchdog = this._document.defaultView?.setTimeout(() => {
      this._watchdog = undefined;
      if (this._phase() === 'authenticated') return;
      console.info('[SSO] no decision within timeout -> failed');
      this._phase.set('failed');
    }, PENDING_TIMEOUT_MS);
  }

  private clearPendingWatchdog(): void {
    if (this._watchdog === undefined) return;
    this._document.defaultView?.clearTimeout(this._watchdog);
    this._watchdog = undefined;
  }

  // Ping may deliver `groups` as a scalar string (single group), an array
  // (multiple), a space/comma-separated string, or omit it. Normalize to string[].
  private normalizeGroups(groups?: string | string[]): string[] {
    if (Array.isArray(groups)) return groups.filter((g) => !!g);
    if (typeof groups === 'string') {
      return groups
        .split(',')
        .map((g) => g.trim())
        .filter((g) => !!g);
    }
    return [];
  }

  private applyIdentity(id: PingIdentity) {
    this._email.set(id.email ?? null);
    this._first.set(id.given_name ?? null);
    this._last.set(id.family_name ?? null);
    // The Roche short ID (e.g. `miczekd`). Distinct from `sub` — see the
    // accessors above. Backends derive X-User from the validated JWT, not
    // from this; it reaches the wire only through the fulfillment payload.
    this._cid.set(id.username ?? id.sub ?? null);
    this._sub.set(id.sub ?? null);
    this._groups.set(this.normalizeGroups(id.groups));

    faro?.api?.setUser({
      id: id.sub ?? 'guest-unknown',
      attributes: {
        ping_sub: id.sub ?? 'guest-unknown',
      },
    });
  }

  private applyMockIdentity(id: MockIdentity) {
    this._email.set(id.email ?? null);
    this._first.set(id.custom?.first ?? null);
    this._last.set(id.custom?.last ?? null);
    this._cid.set(id.custom?.id ?? null);
    this._sub.set(id.sub ?? null);
    this._groups.set(this.normalizeGroups(id.groups));

    // Must match applyIdentity's attribute name. When these two disagreed,
    // everything reading the attribute silently resolved to the sentinel.
    faro?.api?.setUser({
      id: id.sub ?? 'guest-unknown',
      attributes: {
        ping_sub: id.sub ?? 'guest-unknown',
      },
    });
  }

  /**
   * Completes a login that FILM started by sending the user back to FILM.
   *
   * Ping returns every login to the domain root, so a cold visit to
   * `/film/...` ends up here with `?code=`. The exchange succeeds because FILM
   * shares this app's storage entry; what is left is to return the user to the
   * route FILM parked under `FILM_POST_LOGIN_URL_KEY`.
   *
   * Full-page `replace`, not the router: FILM is a different SPA, and this
   * app's wildcard route would swallow `/film/...`. Our own deep-link target is
   * dropped — the user asked for FILM, not for whatever this tab stashed
   * earlier. Phase goes to `redirecting` so the shell stays hidden while the
   * tab unloads; the watchdog armed in `finally` covers a replace that never
   * leaves.
   *
   * @returns true when the tab is on its way to FILM.
   */
  private handOffToFilm(): boolean {
    const storage = this._document.defaultView?.sessionStorage;
    const target = storage?.getItem(FILM_POST_LOGIN_URL_KEY);
    if (!target) return false;
    storage?.removeItem(FILM_POST_LOGIN_URL_KEY);

    // Same-origin path only. `//host` and backslashes would let a value
    // planted in sessionStorage turn this into an open redirect.
    if (!target.startsWith('/') || target.startsWith('//') || target.includes('\\')) {
      console.info('[SSO][refresh] ignoring unsafe FILM hand-off target');
      return false;
    }

    const path =
      target === FILM_BASE_PATH || target.startsWith(`${FILM_BASE_PATH}/`)
        ? target
        : `${FILM_BASE_PATH}${target}`;

    storage?.removeItem(SSO_TARGET_URL_KEY);
    this._phase.set('redirecting');
    console.info('[SSO][refresh] handing off to FILM', { path });
    this._document.location.replace(path);
    return true;
  }

  // authGuard stashes the pre-Ping URL (e.g. /mercury-form/orchest-mssql) in
  // sessionStorage before calling authorize(). Ping's redirectUrl is the SPA
  // so without this the deep-link is lost on the callback.
  private restoreDeepLinkTarget(): void {
    const storage = this._document.defaultView?.sessionStorage;
    const target = storage?.getItem(SSO_TARGET_URL_KEY);
    if (!target) return;

    storage?.removeItem(SSO_TARGET_URL_KEY);

    const currentUrl = this._router.url;
    if (currentUrl === target) {
      console.info('[SSO][refresh] deep-link target matches current url, skipping navigate', { target });
      return;
    }

    console.info('[SSO][refresh] restoring deep-link target', { target, currentUrl });
    this._router.navigateByUrl(target).catch((err: unknown) => {
      console.info(
        '[SSO][refresh] deep-link navigation failed',
        err instanceof Error ? err.message : String(err),
      );
      faro?.api?.pushError(
        err instanceof Error ? err : new Error(String(err)),
      );
    });
  }

  logout(): void {
    if (!environment.pingAuthInit) {
      this._document.location.href = '/';
      return;
    }
    this._oidc.logoff().subscribe();
  }

  private reset(): void {
    this._email.set(null);
    this._first.set(null);
    this._last.set(null);
    this._cid.set(null);
    this._sub.set(null);
    this._groups.set([]);

    faro?.api?.setUser({ id: 'guest-unknown' });
  }
}
